import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, LogIn, LogOut, Calendar, Check, AlertCircle, Home, Coffee, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../utils/apiClient';
import PageContainer from '../layout/PageContainer';

interface AttendanceStatus {
  status: 'not_clocked' | 'clocked_in' | 'clocked_out' | 'on_break';
  attendance: {
    id: number;
    clock_in: string;
    clock_out: string | null;
    break_start?: string | null;
    break_end?: string | null;
    work_hours: number | null;
    work_date?: string;
  } | null;
  checkDate?: string;
}

interface Staff {
  id: number;
  display_name: string;
  username: string;
}

const QuickAttendance: React.FC = () => {
  const navigate = useNavigate();
  const [selectedStaff, setSelectedStaff] = useState<number | null>(null);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [attendanceStatus, setAttendanceStatus] = useState<AttendanceStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchStaffList();
  }, []);

  useEffect(() => {
    if (selectedStaff) {
      fetchAttendanceStatus();
    }
  }, [selectedStaff]);

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/staff/list`);
      console.log('スタッフリスト取得成功:', response.data);
      // APIレスポンスが { success: true, data: [...] } 形式の場合
      if (response.data.success && response.data.data) {
        setStaffList(response.data.data);
      } else if (Array.isArray(response.data)) {
        // 配列で直接返ってきた場合
        setStaffList(response.data);
      } else {
        console.error('予期しないレスポンス形式:', response.data);
        setStaffList([]);
      }
    } catch (error) {
      console.error('スタッフリスト取得エラー:', error);
      setStaffList([]);
    }
  };

  const fetchAttendanceStatus = async () => {
    if (!selectedStaff) return;
    
    try {
      const response = await axios.get(`${getApiUrl()}/attendance/status/${selectedStaff}`);
      console.log('Attendance status response:', response.data);
      
      // not_clocked_in を not_clocked に変換
      if (response.data.status === 'not_clocked_in') {
        response.data.status = 'not_clocked';
      }
      
      setAttendanceStatus(response.data);
    } catch (error) {
      console.error('勤怠状態取得エラー:', error);
      // エラー時はnot_clockedを設定して出勤ボタンを表示
      setAttendanceStatus({ status: 'not_clocked', attendance: null });
    }
  };

  const handleClockIn = async () => {
    if (!selectedStaff) {
      setMessage({ type: 'error', text: 'スタッフを選択してください' });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      await axios.post(`${getApiUrl()}/attendance/clock-in`, {
        staff_id: selectedStaff
      });
      setMessage({ type: 'success', text: '出勤しました！お疲れ様です！' });
      fetchAttendanceStatus();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || '出勤打刻に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const handleBreakStart = async () => {
    if (!selectedStaff) {
      setMessage({ type: 'error', text: 'スタッフを選択してください' });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      await axios.post(`${getApiUrl()}/attendance/break-start`, {
        staff_id: selectedStaff
      });
      setMessage({ type: 'success', text: '休憩を開始しました' });
      fetchAttendanceStatus();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || '休憩開始の記録に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const handleBreakEnd = async () => {
    if (!selectedStaff) {
      setMessage({ type: 'error', text: 'スタッフを選択してください' });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      await axios.post(`${getApiUrl()}/attendance/break-end`, {
        staff_id: selectedStaff
      });
      setMessage({ type: 'success', text: '休憩を終了しました' });
      fetchAttendanceStatus();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || '休憩終了の記録に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!selectedStaff) {
      setMessage({ type: 'error', text: 'スタッフを選択してください' });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      await axios.post(`${getApiUrl()}/attendance/clock-out`, {
        staff_id: selectedStaff
      });
      setMessage({ type: 'success', text: '退勤しました！お疲れ様でした！' });
      fetchAttendanceStatus();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || '退勤打刻に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (datetime: string | null) => {
    if (!datetime) return '-';
    return new Date(datetime).toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatWorkHours = (hours: number | null) => {
    if (!hours) return '-';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}時間${m}分`;
  };

  const currentStaff = staffList.find(s => s.id === selectedStaff);

  return (
    <PageContainer maxWidth="4xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Clock className="h-8 w-8 text-blue-600" />
            クイック勤怠
          </h1>
          <button
            onClick={() => navigate('/home')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors shadow-lg"
          >
            <Home className="w-5 h-5" />
            ホーム
          </button>
        </div>

        {/* スタッフ選択 */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <label className="block text-lg font-semibold text-gray-700 mb-3">
            スタッフを選択
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {staffList.map((staff) => (
              <button
                key={staff.id}
                onClick={() => setSelectedStaff(staff.id)}
                className={`p-4 rounded-lg border-2 font-medium transition-all ${
                  selectedStaff === staff.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {staff.display_name}
              </button>
            ))}
          </div>
        </div>

        {/* メッセージ表示 */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <Check className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        {/* 現在の状態表示 */}
        {selectedStaff && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="text-2xl font-bold text-gray-800 mb-2">
                {currentStaff?.display_name}
              </div>
              <div className="text-4xl font-bold">
                {new Date().toLocaleDateString('ja-JP', {
                  month: 'long',
                  day: 'numeric',
                  weekday: 'long'
                })}
              </div>
            </div>

            {/* ワンタップボタン */}
            <div className="flex justify-center gap-6">
              {(attendanceStatus?.status === 'not_clocked' || attendanceStatus?.status === 'not_clocked_in' || !attendanceStatus?.attendance) && attendanceStatus?.status !== 'clocked_in' && attendanceStatus?.status !== 'clocked_out' && (
                <button
                  onClick={handleClockIn}
                  disabled={loading}
                  className="group relative px-12 py-8 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LogIn className="h-16 w-16 mx-auto mb-3" />
                  <div className="text-2xl font-bold">出勤</div>
                  <div className="text-sm mt-2 opacity-90">タップで打刻</div>
                </button>
              )}

              {attendanceStatus?.status === 'clocked_in' && (
                <>
                  <div className="text-center">
                    <div className="mb-6 p-6 bg-blue-50 rounded-xl">
                      <div className="text-sm text-gray-600 mb-1">出勤時刻</div>
                      <div className="text-3xl font-bold text-blue-600">
                        {formatTime(attendanceStatus.attendance?.clock_in || null)}
                      </div>
                    </div>
                    
                    <div className="flex gap-4 justify-center">
                      {/* 休憩開始ボタン（休憩中でない場合のみ表示） */}
                      {!attendanceStatus.attendance?.break_start || attendanceStatus.attendance?.break_end ? (
                        <button
                          onClick={handleBreakStart}
                          disabled={loading}
                          className="group relative px-8 py-6 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Coffee className="h-12 w-12 mx-auto mb-2" />
                          <div className="text-xl font-bold">休憩開始</div>
                          <div className="text-xs mt-1 opacity-90">中抜け</div>
                        </button>
                      ) : (
                        /* 休憩終了ボタン（休憩中の場合） */
                        <button
                          onClick={handleBreakEnd}
                          disabled={loading}
                          className="group relative px-8 py-6 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Play className="h-12 w-12 mx-auto mb-2" />
                          <div className="text-xl font-bold">休憩終了</div>
                          <div className="text-xs mt-1 opacity-90">仕事再開</div>
                        </button>
                      )}
                      
                      <button
                        onClick={handleClockOut}
                        disabled={loading}
                        className="group relative px-8 py-6 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <LogOut className="h-12 w-12 mx-auto mb-2" />
                        <div className="text-xl font-bold">退勤</div>
                        <div className="text-xs mt-1 opacity-90">タップで打刻</div>
                      </button>
                    </div>
                    
                    {/* 休憩中の表示 */}
                    {attendanceStatus.attendance?.break_start && !attendanceStatus.attendance?.break_end && (
                      <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                        <div className="flex items-center justify-center gap-2 text-yellow-700">
                          <Coffee className="h-5 w-5" />
                          <span className="font-semibold">休憩中</span>
                          <span>（開始: {formatTime(attendanceStatus.attendance.break_start)}）</span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {attendanceStatus?.status === 'clocked_out' && (
                <div className="text-center w-full">
                  <div className="bg-gray-50 rounded-xl p-8">
                    <Check className="h-16 w-16 mx-auto text-green-500 mb-4" />
                    <div className="text-2xl font-bold text-gray-800 mb-4">
                      {attendanceStatus.attendance?.work_date && 
                       new Date(attendanceStatus.attendance.work_date).toDateString() === new Date().toDateString()
                        ? '本日の勤務終了'
                        : attendanceStatus.attendance?.work_date
                          ? `${new Date(attendanceStatus.attendance.work_date).toLocaleDateString('ja-JP')}の勤務終了`
                          : '勤務終了'}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="bg-white rounded-lg p-4">
                        <div className="text-sm text-gray-600">出勤</div>
                        <div className="text-xl font-bold">
                          {formatTime(attendanceStatus.attendance?.clock_in || null)}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <div className="text-sm text-gray-600">退勤</div>
                        <div className="text-xl font-bold">
                          {formatTime(attendanceStatus.attendance?.clock_out || null)}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <div className="text-sm text-gray-600">勤務時間</div>
                        <div className="text-xl font-bold text-blue-600">
                          {formatWorkHours(attendanceStatus.attendance?.work_hours || null)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {!selectedStaff && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-gray-400 mb-4">
              <Clock className="h-24 w-24 mx-auto" />
            </div>
            <div className="text-xl text-gray-600">
              上のボタンからスタッフを選択してください
            </div>
          </div>
        )}
    </PageContainer>
  );
};

export default QuickAttendance;